// ═══ EACH PARCEL'S FINAL VALUE FOR A YEAR — for the Properties list (Philip, 2026-10-11) ═══
// The list's Market Value / Taxable Value read parcel_values (scanned notices) for the hardcoded current TAX_YEAR,
// whatever year was selected — and imported values (DTAG/Hertz, scripts/import-ptms.mjs --values) live in
// parcel_appeals, never parcel_values. So every imported property read "—" in every year, Florida included.
//
// Now the same answer as the parcel card and the reports (reports/parcelYearRows primaryLevels + utils/finalLevel):
//   the parcel-year's FINAL level — the deepest level with values, a withdrawn appeal level skipped (resolveFinalLevel,
//   with the year's decisions and Appeal Status) — and that level's PRIMARY column (primaryAppealRow): market_total and
//   taxable_total. No level with values → the Proposed column, as the reports do.
//   A parcel-year with NO parcel_appeals rows at all falls back to its scanned notice (parcel_values, primary row
//   first) — what the card shows for it (seeded, unsaved) and what this list showed before, so nothing that read a
//   figure goes blank.
import { supabase } from '../supabase';
import { pageAll } from './pageAll';
import { resolveFinalLevel } from './finalLevel';
import { ORIGINAL_NOTICE } from './notices';
import { primaryAppealRow } from './appealRows';

const num = (v) => { if (v == null || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; };

// Pure. appeals: parcel_appeals rows (one year) · decisions: parcel_appeal_decisions rows · statuses: Map<parcel_id,
// appeal_status> · notices: parcel_values rows (primary first). → Map<parcel_id, { market, taxable, source }>
export function finalValuesByParcel({ appeals = [], decisions = [], statuses = new Map(), notices = [] }) {
  const byParcel = new Map();
  appeals.forEach((r) => {
    const lv = byParcel.get(r.parcel_id) || {};
    (lv[r.appeal_level] = lv[r.appeal_level] || []).push(r);
    byParcel.set(r.parcel_id, lv);
  });
  const decByParcel = new Map();
  decisions.forEach((d) => {
    const m = decByParcel.get(d.parcel_id) || {};
    m[d.appeal_level] = d.decision;
    decByParcel.set(d.parcel_id, m);
  });
  const out = new Map();
  byParcel.forEach((levels, pid) => {
    const { level } = resolveFinalLevel({ levels, decisions: decByParcel.get(pid) || {}, status: statuses.get(pid) ?? null });
    const row = primaryAppealRow(levels[level] || levels.proposed || [], `properties list ${pid}`);
    out.set(pid, { market: num(row?.market_total), taxable: num(row?.taxable_total), source: 'appeals' });
  });
  notices.forEach((pv) => {
    if (!out.has(pv.parcel_id)) out.set(pv.parcel_id, { market: num(pv.market_value), taxable: num(pv.taxable_value), source: 'notice' });
  });
  return out;
}

// Every parcel's final value for `year`, paged past the 1000-row cap.
export async function loadYearValues(year) {
  const y = String(year);
  const [appeals, decisions, years, notices] = await Promise.all([
    pageAll(() => supabase.from('parcel_appeals')
      .select('id, parcel_id, appeal_level, jurisdiction, is_primary, market_land, market_imp, market_pp, market_total, taxable_total')
      .eq('tax_year', y).eq('notice_no', ORIGINAL_NOTICE).order('id')),
    pageAll(() => supabase.from('parcel_appeal_decisions').select('id, parcel_id, appeal_level, decision').eq('tax_year', y).eq('notice_no', ORIGINAL_NOTICE).order('id')),
    pageAll(() => supabase.from('parcel_year_data').select('id, parcel_id, appeal_status').eq('tax_year', y).order('id')),
    pageAll(() => supabase.from('parcel_values').select('id, parcel_id, market_value, taxable_value, is_primary')
      .eq('tax_year', y).eq('notice_no', ORIGINAL_NOTICE).order('is_primary', { ascending: false }).order('id')),
  ]);
  return finalValuesByParcel({ appeals, decisions, notices, statuses: new Map(years.map((r) => [r.parcel_id, r.appeal_status])) });
}
